import { useAppDispatch } from 'app/hooks';
import { REPRODUCE_ERROR_LOG_PARAM } from 'data/error-log-constants';
import { useLazyGetErrorLogQuery } from 'features/api/error-log-api';
import { clearStructureHistory } from 'features/structure';
import { setReproducingErrorLogId } from 'features/ui';
import { useEffect, useLayoutEffect, useState } from 'react';
import { ReproduceStatusType } from 'types/error-log';
import { isCompatibleReduxSnapshot } from 'utils/error-log/is-compatible-redux-snapshot';
import { hydrateReduxSnapshot } from 'utils/error-log/redux-snapshot';
import { getReproduceErrorLogId } from 'utils/error-log/reproduce';
import { removeQueryParam } from 'utils/helpers';

type ErrorLogReproduceWatcherProps = {
  loading: boolean;
};

export default function ErrorLogReproduceWatcher({
  loading
}: ErrorLogReproduceWatcherProps) {
  const dispatch = useAppDispatch();
  const [errorLogId] = useState(() => getReproduceErrorLogId());
  const [status, setStatus] = useState<ReproduceStatusType>(
    errorLogId === null ? 'idle' : 'pending'
  );
  const [droppedSlices, setDroppedSlices] = useState<string[]>([]);
  const [fetchErrorLog] = useLazyGetErrorLogQuery();

  useLayoutEffect(() => {
    if (errorLogId === null) {
      return;
    }
    dispatch(setReproducingErrorLogId(errorLogId));
  }, [dispatch, errorLogId]);

  useEffect(() => {
    if (errorLogId === null || loading || status !== 'pending') {
      return undefined;
    }
    let cancelled = false;

    const fail = () => {
      if (!cancelled) {
        // Keep reproducingErrorLogId set. Clearing it after `loading` is
        // already false remounts ResetDataWatcher for the first time
        // post-load; its reset effects would treat the live project as a
        // user edit and clear structure, configurations, and systems.
        setStatus('failed');
      }
    };

    const hydrate = async () => {
      try {
        const log = await fetchErrorLog(errorLogId).unwrap();
        if (cancelled) {
          return;
        }
        if (!log.reduxState || !isCompatibleReduxSnapshot(log.reduxState)) {
          fail();
          return;
        }
        // Wait until initializeApp has finished (`loading` is false) so the
        // live project load cannot overwrite this snapshot. Mutating watchers
        // must already be unmounted — they key off reproducingErrorLogId
        // (set in useLayoutEffect) and the reproduce URL param — or they
        // treat this hydrate as a user edit and wipe selected systems.
        dispatch(hydrateReduxSnapshot(log.reduxState));
        dispatch(clearStructureHistory());
        setDroppedSlices(log.reduxState.meta?.droppedSlices ?? []);
        setStatus('hydrated');
        removeQueryParam(REPRODUCE_ERROR_LOG_PARAM);
      } catch {
        fail();
      }
    };

    hydrate();

    return () => {
      cancelled = true;
    };
  }, [dispatch, errorLogId, fetchErrorLog, loading, status]);

  if (errorLogId === null) {
    return null;
  }

  return (
    <div className='flex w-full flex-col items-center justify-center bg-purple-200 py-2 text-sm font-semibold text-purple-900'>
      {status === 'pending' && (
        <span>Loading the Redux snapshot for error log #{errorLogId}…</span>
      )}
      {status === 'hydrated' && (
        <span>
          Reproducing error log #{errorLogId} — the Redux snapshot has been
          loaded. Auto-save is disabled, but actions that call the API can still
          change this project.
        </span>
      )}
      {status === 'hydrated' && droppedSlices.length > 0 && (
        <span className='font-normal'>
          Not restored (snapshot size limit): {droppedSlices.join(', ')}
        </span>
      )}
      {status === 'failed' && (
        <span>
          Could not load the snapshot for error log #{errorLogId}; showing the
          live project instead.
        </span>
      )}
    </div>
  );
}
